import {
    CalendarToday as CalendarIcon,
    Email as EmailIcon,
    Info as InfoIcon,
    People as PeopleIcon,
    Share as ShareIcon,
    TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import {
    Alert,
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    LinearProgress,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Paper,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNotification } from '../../context/NotificationContext';
import { WaitlistService, WaitlistStats } from '../../services/api/waitlistService';

interface WaitlistAnalyticsProps {
  // Add any props if needed
}

interface AnalyticsData {
  stats: WaitlistStats | null;
  recentActivity: Array<{
    id: string;
    name: string;
    email: string;
    referral_code: string;
    created_at: Date;
    referral_count: number;
  }>;
  conversionRate: number;
  averageReferrals: number;
  topReferrers: Array<{
    name: string;
    email: string;
    referral_count: number;
    referral_code: string;
  }>;
}

export const WaitlistAnalytics: React.FC<WaitlistAnalyticsProps> = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const { showNotification } = useNotification();

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const stats = await WaitlistService.getWaitlistStats();
      
      // Calculate additional metrics
      const conversionRate = stats.total_entries > 0 
        ? ((stats.active_entries / stats.total_entries) * 100).toFixed(1)
        : '0';
      
      const averageReferrals = stats.top_referrers.length > 0
        ? (stats.top_referrers.reduce((sum, ref) => sum + ref.referral_count, 0) / stats.top_referrers.length).toFixed(1)
        : '0';

      // Get recent activity (last 10 entries)
      const recentActivity = await WaitlistService.getWaitlistEntries({ limit: 10 });
      
      setAnalyticsData({
        stats,
        recentActivity: recentActivity.entries,
        conversionRate: parseFloat(conversionRate),
        averageReferrals: parseFloat(averageReferrals),
        topReferrers: stats.top_referrers
      });
    } catch (error) {
      showNotification('Failed to load analytics data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!analyticsData) {
    return (
      <Alert severity="error">
        Failed to load analytics data
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Sticky Coming Soon Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          backgroundColor: '#3456D5',
          py: 1.5,
          px: 2,
          mb: 3,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          boxShadow: 2,
        }}
      >
        <InfoIcon sx={{ color: '#fff', fontSize: 20 }} />
        <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
          <strong>Coming Soon:</strong> This page is under development. All data shown is dummy and not functional.
        </Typography>
      </Box>

      <Typography variant="h4" color="text.primary" gutterBottom>
        Waitlist Analytics
      </Typography>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{xs: 12, sm: 6, md: 3}}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Total Entries</Typography>
              </Box>
              <Typography variant="h3" color="primary">
                {analyticsData.stats?.total_entries || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                All time waitlist signups
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{xs: 12, sm: 6, md: 3}}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="h6">Active Entries</Typography>
              </Box>
              <Typography variant="h3" color="success.main">
                {analyticsData.stats?.active_entries || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Currently active members
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{xs: 12, sm: 6, md: 3}}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ShareIcon sx={{ mr: 1, color: 'info.main' }} />
                <Typography variant="h6">Avg Referrals</Typography>
              </Box>
              <Typography variant="h3" color="info.main">
                {analyticsData.averageReferrals}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Average referrals per user
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{xs: 12, sm: 6, md: 3}}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6">Conversion Rate</Typography>
              </Box>
              <Typography variant="h3" color="warning.main">
                {analyticsData.conversionRate}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Active vs total entries
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Top Referrers */}
        <Grid size={{xs: 12, sm: 6, md: 6}}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top Referrers
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Users with the most successful referrals
            </Typography>
            
            {analyticsData.topReferrers.length === 0 ? (
              <Alert severity="info">No referrals yet</Alert>
            ) : (
              <List>
                {analyticsData.topReferrers.map((referrer, index) => (
                  <React.Fragment key={referrer.referral_code}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: index < 3 ? 'primary.main' : 'grey.300' }}>
                          {index + 1}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={referrer.name}
                        secondary={referrer.email}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={`${referrer.referral_count} referrals`}
                          color={referrer.referral_count > 5 ? 'success' : 'default'}
                          size="small"
                        />
                        <Chip 
                          label={referrer.referral_code}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    </ListItem>
                    {index < analyticsData.topReferrers.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid size={{xs: 12, sm: 6, md: 6}}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Latest waitlist signups
            </Typography>
            
            {analyticsData.recentActivity.length === 0 ? (
              <Alert severity="info">No recent activity</Alert>
            ) : (
              <List>
                {analyticsData.recentActivity.map((entry, index) => (
                  <React.Fragment key={entry.id}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <CalendarIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={entry.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              {entry.email}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {formatDate(entry.created_at)}
                            </Typography>
                          </Box>
                        }
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={entry.referral_code}
                          variant="outlined"
                          size="small"
                        />
                        {entry.referral_count > 0 && (
                          <Chip 
                            label={`${entry.referral_count} refs`}
                            color="success"
                            size="small"
                          />
                        )}
                      </Box>
                    </ListItem>
                    {index < analyticsData.recentActivity.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Conversion Rate Progress */}
        <Grid size={{xs: 12, sm: 6, md: 6}}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Conversion Rate Analysis
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Active entries vs total entries
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  Active Entries: {analyticsData.stats?.active_entries || 0}
                </Typography>
                <Typography variant="body2">
                  {analyticsData.conversionRate}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={analyticsData.conversionRate} 
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            
            <Grid container spacing={2}>
              <Grid size={{xs: 12, sm: 6}}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" color="success.main">
                      {analyticsData.stats?.active_entries || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Active Members
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{xs: 12, sm: 6}}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" color="textSecondary">
                      {(analyticsData.stats?.total_entries || 0) - (analyticsData.stats?.active_entries || 0)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Inactive Members
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}; 